import React, { useState, useRef } from 'react';
import { Upload, Button, Image, Modal, message, Tooltip } from 'antd';
import { UploadOutlined, CameraOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import ImageEditorModal from './ImageGallery/ImageEditing';
import { storeImageLocally, updateImageInModule } from '@/stores/slice/reportSlice/inspectionSlice';
import { useDispatch } from 'react-redux';
import toast from "react-hot-toast";

const ImageDropZone = ({
  stepIndex,
  unitIndex = null,
  componentName,
  currentImage,
  placeholderImage,
  isCommon = false,
  required = false,
  onImageChange,
  onRemoveImage,
  onDragOver,
  onDragLeave,
  onDrop,
  dragOverTarget, moduleKey
}) => {

  const [cameraModalVisible, setCameraModalVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const dragCounterRef = useRef(0);
  const [editorVisible, setEditorVisible] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const dispatch = useDispatch()
  // Check if this dropzone is currently being dragged over
  const isDraggedOver = dragOverTarget?.stepIndex === stepIndex &&
    dragOverTarget?.unitIndex === unitIndex &&
    dragOverTarget?.componentName === componentName &&
    dragOverTarget?.isCommon === isCommon;

  // Handle file upload
  const handleUpload = async ({ fileList }) => {
    try {
      const file = fileList[0]?.originFileObj || fileList[0];
      if (file && file instanceof File) {
        await onImageChange(file, stepIndex, unitIndex, componentName, isCommon, false);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      setCameraModalVisible(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Failed to access camera');
      setCameraModalVisible(false);
    }
  };

  const capturePhoto = async () => {
    try {
      if (!videoRef.current || !canvasRef.current) return;

      const canvas = canvasRef.current;
      const video = videoRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
          await onImageChange(file, stepIndex, unitIndex, componentName, isCommon, true);
        }
      }, 'image/jpeg', 0.8);

      stopCamera();
    } catch (error) {
      console.error('Capture error:', error);
      toast.error('Failed to capture photo');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraModalVisible(false);
  };

  // Enhanced drag and drop handlers with better tracking
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;

    if (dragCounterRef.current === 1) {
      setIsDragging(true);
      if (onDragOver) {
        onDragOver(e, stepIndex, unitIndex, componentName, isCommon);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;

    if (dragCounterRef.current === 0) {
      setIsDragging(false);
      if (onDragLeave) {
        onDragLeave(e);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Reset drag state
    dragCounterRef.current = 0;
    setIsDragging(false);

 
    try {
      // Try to get drag data in multiple formats
      let dragData = null;
      let imageData = null;

      const formats = ['application/json', 'text/plain', 'text/html'];

      for (const format of formats) {
        const data = e.dataTransfer.getData(format);
        if (data) {
          try {
            dragData = JSON.parse(data);
            imageData = dragData.imageData || dragData;
             break;
          } catch (parseError) {
            console.warn('⚠️ ImageDropZone: Failed to parse data from format:', format, parseError);
          }
        }
      }

      // Fallback: try to reconstruct from individual pieces
      if (!imageData) {
        const imageId = e.dataTransfer.getData('image/id');
        const imageUrl = e.dataTransfer.getData('image/url');
        const imageName = e.dataTransfer.getData('image/name');

        if (imageId && imageUrl) {
          imageData = {
            id: imageId,
            url: imageUrl,
            name: imageName || 'Unknown',
            uploaded: true
          };
         }
      }

      // Handle file drops from outside the application
      if (!imageData && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        const imageFile = files.find(file => file.type.startsWith('image/'));

        if (imageFile) {
           onImageChange(imageFile, stepIndex, unitIndex, componentName, isCommon, false);
          return;
        }
      }

      if (!imageData) {
        console.error('❌ ImageDropZone: No valid image data found in drop event');
        toast.error('Failed to assign image - no valid data found');
        return;
      }

      // Validate image data structure
      if (!imageData.id || !imageData.url) {
        console.error('❌ ImageDropZone: Invalid image data structure:', imageData);
        toast.error('Invalid image data - missing required fields');
        return;
      }

      // Call the parent drop handler
      if (onDrop) {
        onDrop(e, stepIndex, unitIndex, componentName, isCommon);
      } else {
        // Direct assignment if no parent handler
         // You might want to call onImageChange here or handle the assignment directly
        toast.success(`Image assigned to ${isCommon ? 'Common' : `Unit ${unitIndex + 1}`} - ${componentName}`);
      }

    } catch (error) {
      console.error('💥 ImageDropZone: Drop error:', error);
      toast.error('Failed to assign image: ' + error.message);
    }
  };

  const handleRemove = () => {
    onRemoveImage(stepIndex, unitIndex, componentName, isCommon);
  };

  // Enhanced drop zone styling
  const dropZoneStyle = {
    border: (isDraggedOver || isDragging) ? '3px dashed #1890ff' :
      currentImage ? '2px solid #52c41a' : '2px dashed #d9d9d9',
    borderRadius: '8px',
    padding: '2px',
    textAlign: 'center',
    backgroundColor: (isDraggedOver || isDragging) ? '#f0f7ff' :
      currentImage ? '#f6ffed' : '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    transition: 'all 0.3s ease',
    cursor: (isDraggedOver || isDragging) ? 'copy' : 'default'
  };

  const handleEditImage = (image, moduleKey) => {
    // Ensure we have proper image data
    const imageToEdit = {
      ...image,
      moduleKey,
      // Ensure URL is properly formatted
      url: image.url || image.src || image.preview
    };

     setEditingImage(imageToEdit);
    setEditorVisible(true);
  };
  const handleImageEdit = async (editedFile, originalImageId, moduleKey) => {
    try {
      // Generate new ID for the edited image
      const newImageId = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Store the edited image locally
      const editedImageData = await storeImageLocally(editedFile, newImageId);
       // Update Redux state - replace the original image with the edited one
      dispatch(updateImageInModule({
        module: moduleKey || 'general',
        imageId: originalImageId,
        updatedImageData: {
          ...editedImageData,
          editedAt: new Date().toISOString(),
          originalImageId: originalImageId,
        }
      }));
 
    } catch (error) {
      console.error('Error handling image edit:', error);
      throw error;
    }
  };


  const handleSaveEdit = async (editedFile, originalImageId, moduleKey) => {
    try {
      await handleImageEdit(editedFile, originalImageId, moduleKey);
      toast.success('Image edited successfully!');
      setEditorVisible(false);
      setEditingImage(null);
    } catch (error) {
      console.error('Edit save error:', error);
      toast.error('Failed to save edited image');
    }
  };


  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
         {required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
      </div>
      {/* Image Editor Modal */}
      <ImageEditorModal
        visible={editorVisible}
        image={editingImage}
        onSave={handleSaveEdit}
        onCancel={() => {
          setEditorVisible(false);
          setEditingImage(null);
        }}
      />
      <div
        style={dropZoneStyle}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {currentImage ? (
          <div style={{ position: 'relative', width: '100%' }}>
            <Image
              src={currentImage.url}
              alt={currentImage.name}
              style={{
                width: '100%',
                maxHeight: '100px',
                objectFit: 'cover',
                borderRadius: '4px'
              }}
              preview={{
                mask: <div style={{ fontSize: '12px' }}>Preview</div>
              }}
            />



            <div style={{
              position: 'absolute',
              bottom: '2px',
              left: '2px',
              right: '2px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '2px',
            }}>
              {/* Edit button */}
              <Tooltip title="Edit image">
                <Button
                  type="text"
                  icon={<EditOutlined style={{ fontSize: "16px" }} />}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEditImage(currentImage, moduleKey);
                  }}
                  style={{
                    backgroundColor: 'rgba(24, 144, 255, 0.1)',
                    border: 'none',
                    width: '18px',
                    height: '18px',
                    minWidth: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    borderRadius: '4px'
                  }}
                />
              </Tooltip>

              {/* Delete button */}

              <Tooltip title="Delete image">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined style={{ fontSize: "16px" }} />}
                  onClick={(e) => {

                    onClick = { handleRemove }
                  }}
                  style={{
                    backgroundColor: 'rgba(255, 77, 79, 0.1)',
                    border: 'none',
                    width: '18px',
                    height: '18px',
                    minWidth: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    borderRadius: '4px'
                  }}
                />
              </Tooltip>

            </div>


          </div>
        ) : (
          <div>
            {(isDraggedOver || isDragging) ? (
              <div style={{ color: '#1890ff', fontSize: '14px', fontWeight: 'bold' }}>
                Drop image here
              </div>
            ) : (
              <>
                {placeholderImage && (
                  <div style={{ marginBottom: '8px', opacity: 0.5 }}>
                    <Image
                      src={placeholderImage}
                      alt="placeholder"
                      width={60}
                      height={40}
                      style={{ objectFit: 'cover', borderRadius: '4px' }}
                    />
                  </div>
                )}

                <div style={{ color: '#8c8c8c', fontSize: '12px', marginBottom: '8px' }}>
                  Drop image here or
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <Upload
                    showUploadList={false}
                    beforeUpload={() => false}
                    onChange={handleUpload}
                    accept="image/*"
                  >
                    <Button size="small" className='text-xs' icon={<UploadOutlined />}>
                      <span className='text-xs' >Upload</span>
                    </Button>
                  </Upload>

                  <Button
                    size="small"
                    icon={<CameraOutlined />}
                    onClick={startCamera}
                  >
                    <span className='text-xs' >Camera</span>

                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Camera Modal */}
      <Modal
        title="Capture Photo"
        open={cameraModalVisible}
        onCancel={stopCamera}
        footer={[
          <Button key="cancel" onClick={stopCamera}>
            Cancel
          </Button>,
          <Button key="capture" type="primary" onClick={capturePhoto} icon={<CameraOutlined />}>
            Capture
          </Button>
        ]}
        width={500}
        destroyOnClose
      >
        <div style={{ textAlign: 'center' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{
              width: '100%',
              maxWidth: '400px',
              height: 'auto',
              borderRadius: '8px',
              backgroundColor: '#000'
            }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      </Modal>
    </div >
  );
};

export default ImageDropZone;